import { eventChannel, takeLatest, takeEvery, delay, buffers } from 'redux-saga'
import {
	select,
	fork,
	spawn,
	cancel,
	take,
	call,
	put,
	race,
	cancelled,
	actionChannel,
} from 'redux-saga/effects'
import Jszip from 'jszip'
import actionTypes from '../actions/actionTypes'
import config from 'config'
import sessionTypes from 'helpers/enums/sessionTypes'
import { studentSync, loginSync } from 'sync'
import { studentSelectors } from 'redux/selectors'
import tooltipSelector from 'redux/selectors/tooltips/tooltips_selector'
import studentActions from 'redux/actions/studentActions'
import loginActions from 'redux/actions/loginActions'
import callAny from './helpers/call_any'
import { localize } from 'helpers/translator'
import { getPollAnswersData, savePollQuestionData } from 'helpers/cache/poll_local_storage'
import { getQuizAnswersData, saveQuizQuestionData } from 'helpers/cache/quiz_local_storage'
import {
	addLead,
	getLead,
	addLastNicknameUserLoggedSigned,
	getLastNicknameUserLoggedSigned,
} from 'helpers/cache/lead_local_storage'
import {
	getOeqAnswersData,
	saveOeqQuestionData,
	saveOeqExtraData,
	getOeqExtraData,
} from 'helpers/cache/oeq_local_storage'
import { getDrawItAnswersData, saveDrawItQuestionData } from 'helpers/cache/draw_it_local_storage'
import { deleteFile } from 'helpers/fileSystem'
import socketConnect from 'sync/helpers/socketConnection'
import updateMatchingPairsStudentResponse from 'sync/matching_pairs/update_student_response'
import formatError from './helpers/error_formatter'
import mixpanel from 'helpers/mixpanel'
import makeUniqueId from 'helpers/makeUniqueId'
import {
	submitStudentAudio,
	getStudentAudio,
} from 'helpers/submitStudentAudio'

import student_draw_it from './helpers/student_draw_it'
import userLoggedSelector from 'redux/selectors/user_extra_data/user_logged_selector'

function *shouldSubmit() {
	//Types of sessions that needs submission
	const sessionTypesSubmit = [sessionTypes.LIVE_SESSION, sessionTypes.HOMEWORK]
	const sessionType = yield select(studentSelectors.studentSessionTypeSelector)
	return sessionTypesSubmit.includes(sessionType)
}

function *submitPollAnswers(action) {

	const canSubmit = yield call(shouldSubmit)
	if (!canSubmit) {
		return
	}
	try {
		if (action.sessionType === sessionTypes.EMBED ||
			action.sessionType === sessionTypes.PREVIEW ||
			action.sessionType === sessionTypes.TEACHER_STUDENT_VIEW) {
			//We don't want to submit if it's a Preview, Embed, or a Teacher student view
			return
		}

		const submitData = yield select(studentSelectors.pollSubmitSelector, action.slide_uid)

		const has_answers = Object.keys(submitData.answers).findIndex(
			key => submitData.answers[key] === '1'
		)

		if (has_answers > -1 ||
			action.type === actionTypes.STUDENT_SUBMIT_POLL_CONFIRMED) {
			const slideData = action.slide_data

			if (slideData.hasChanged) {
				const sessionData = yield select(studentSelectors.studentSessionDataSelector)
				yield spawn(
					savePollQuestionData,
					submitData,
					sessionData,
					slideData
				)

				yield callAny(
					studentSync.submitPoll,
					submitData.device_uid,
					slideData.slideIndex,
					submitData.headers,
					submitData.answers
				)
			}
		}
	} catch (err) {
		const serverError = formatError(err)
		yield put(studentActions.studentSubmissionFailed(serverError, action.slide_uid, action.sessionType))
	}
}

function *submitQuizAnswers(action) {

	const canSubmit = yield call(shouldSubmit)
	if (!canSubmit) {
		return
	}
	try {
		const sessionData = yield select(studentSelectors.studentSessionDataSelector)
		if (sessionData.sessionType === sessionTypes.PREVIEW || sessionData.sessionType === sessionTypes.TEACHER_STUDENT_VIEW) {
			//We don't want to submit if it's a Preview or a Teacher student view
			return
		}
		if (action.slide_data.hasChanged) {
			const questionIndex = action.type === actionTypes.STUDENT_QUIZ_CHANGE_SLIDE ?
				action.currentIndex :
				action.slide_data.currentQuestionIndex
			const submitData = yield select(
				studentSelectors.quizSubmitSelector,
				action.slide_uid,
				questionIndex
			)

			yield spawn(
				saveQuizQuestionData,
				submitData,
				sessionData,
				action.slide_data
			)
			yield callAny(
				studentSync.submitQuiz,
				submitData.device_uid,
				action.slide_data.slideIndex,
				questionIndex + 1,
				submitData.headers,
				submitData.answers,
				action.slide_data.questions.length
			)
		}
	} catch (err) {
		const serverError = formatError(err)
		yield put(studentActions.studentSubmissionFailed(serverError, action.slide_uid, action.sessionType))
	}
}

function *submitOeqAnswer(action) {
	const canSubmit = yield call(shouldSubmit)
	if (!canSubmit) {
		return
	}
	try {
		if (action.sessionType === sessionTypes.PREVIEW || action.sessionType === sessionTypes.TEACHER_STUDENT_VIEW) {
			//We don't want to submit if it's a Preview or a Teacher student view
			return
		}

		let submitData = yield select(studentSelectors.oeqSubmitSelector, action.slide_uid)
		if (submitData.audioClientUid && !submitData.audioUid) {
			let isSubmissionEnded = false
			while (!isSubmissionEnded) {
				const audioAction = yield take([
					actionTypes.STUDENT_OEQ_RECORDING_SUBMISSION_SUCCESS,
					actionTypes.STUDENT_OEQ_RECORDING_SUBMISSION_FAILED,
				])

				if (audioAction.clientUid === submitData.audioClientUid) {
					if (audioAction.type === actionTypes.STUDENT_OEQ_RECORDING_SUBMISSION_SUCCESS) {
						submitData = {
							...submitData,
							audioUid: audioAction.uid,
						}
					}
					isSubmissionEnded = true
				}
			}
		}

		const has_answers = Object.keys(submitData.answers).findIndex(
			key => submitData.answers[key] !== ''
		)
		if (has_answers > -1
			|| action.type === actionTypes.STUDENT_SUBMIT_OEQ_CONFIRMED
			|| submitData.audioUid) {
			const slideData = action.slide_data

			if (slideData.hasChanged) {
				const sessionData = yield select(studentSelectors.studentSessionDataSelector)

				yield spawn(
					saveOeqExtraData,
					submitData,
					sessionData,
					slideData
				)

				yield spawn(
					saveOeqQuestionData,
					submitData,
					sessionData,
					slideData
				)

				yield callAny(
					studentSync.submitOeq,
					submitData.device_uid,
					slideData.slideIndex,
					submitData.headers,
					submitData.answers,
					submitData.audioUid,

				)
			}
		}
	} catch (err) {
		const serverError = formatError(err)
		yield put(studentActions.studentSubmissionFailed(serverError, action.slide_uid, action.sessionType))
	}
}

function *submitDrawitAnswer(action) {

	const canSubmit = yield call(shouldSubmit)
	if (!canSubmit) {
		return
	}
	const slideData = action.slide_data
	const shouldAvoidAutoSubmit = action.isAutoSubmit && slideData.isPristine

	try {
		if (
			action.sessionType !== sessionTypes.EMBED &&
			action.sessionType !== sessionTypes.PREVIEW &&
			!shouldAvoidAutoSubmit
		) {
			const submitData = yield select(studentSelectors.drawItSubmitSelector, action.slide_uid)
			const answers = submitData.answers
			if (submitData.answers.length || !slideData.isSubmissionRequired) {

				const imageData = yield call(student_draw_it, answers, submitData.backgroundImage)

				const sessionData = yield select(studentSelectors.studentSessionDataSelector)
				yield spawn(
					saveDrawItQuestionData,
					submitData,
					sessionData,
					slideData.slideIndex,
					answers
				)

				yield callAny(
					studentSync.submitDrawIt,
					submitData.device_uid,
					slideData.slideIndex,
					0,
					action.sessionUid, //@TODO: check where this is
					submitData.headers,
					imageData
				)
			}
		}
	} catch (err) {
		const serverError = formatError(err)
		yield put(studentActions.studentSubmissionFailed(serverError, action.slide_uid, action.sessionType))
	}

}


const submittingDrawingActions = [
	actionTypes.STUDENT_SUBMIT_DRAWIT_CONFIRMED,
	actionTypes.STUDENT_TIMER_DRAW_IT_ENDED,
]

const updateStudentDrawingAnswer = (function() {
	let lastAnswers
	return function *updateStudentDrawingAnswer(action) {
		try {
			const sessionType = action.session.sessionType
			if (sessionType === sessionTypes.LIVE_SESSION) {
				const {isDelayFullfilled} = yield race({
					submit: take(submittingDrawingActions),
					isDelayFullfilled: call(delay, 3000),
				})
				if (isDelayFullfilled) {
					const slideData = action.slide_data
					const submitData = yield select(studentSelectors.drawItSubmitSelector, action.slide_uid)
					const answers = submitData.answers
					if (lastAnswers !== answers) {
						lastAnswers = answers
						const zipFile = new Jszip()
						const serializedAnswers = JSON.stringify(answers)
						zipFile.file('answers', serializedAnswers)
						const blobData = yield call(zipFile.generateAsync.bind(zipFile),
							{
								type: 'blob',
								compression: 'DEFLATE',
								compressionOptions: {
									level: 9,
								},
							}
						)

						yield callAny(
							studentSync.submitDrawItPreview,
							submitData.device_uid,
							slideData.slideIndex,
							submitData.headers,
							blobData,
							blobData.size,
						)
					}
				}

			}
		} catch (err) {
		}
	}
}())

function flatteningActivityBuffer() {

	const _actions = []
	return {
		put: action => {
			if (submittingDrawingActions.includes(action.type)) {
				_actions.length = 0
				_actions.push(action)
			} else if (!_actions.length || _actions[_actions.length - 1].type !== action.type) {
				_actions.push(action)
			}
		},
		take: () => _actions.pop(),
		flush: () => {
			_actions.length = 0
		},
		isEmpty: () => _actions.length === 0,
	}
}

function *studentDrawingSubmissions() {
	const requestChannel = yield actionChannel([
		actionTypes.STUDENT_UPDATE_DRAW_IT_ANSWERS,
		...submittingDrawingActions,
	], flatteningActivityBuffer())
	while (true) {
		const action = yield take(requestChannel)
		try {
			if (action.type === actionTypes.STUDENT_UPDATE_DRAW_IT_ANSWERS) {
				yield call(updateStudentDrawingAnswer, action)
			} else {
				yield call(submitDrawitAnswer, action)
			}
		} catch (err) {

		}
	}
}

function *getTimer(action) {
	try {
		const timer = yield callAny(studentSync.getTimer, action.session.device_uid, action.slide.slideId, action.session.headers)
		yield put(studentActions.timerUpdated(timer.payload.status, action.slide_uid))
		return true
	} catch (err) {
		return false
	}

}

function *startTimerPolling(action) {
	while (true) {
		// Instead of using call or fork,
		// we are using spawn to prevent that cancelling this generator will cancel the getTimer call
		const timerCall = yield spawn(getTimer, action)
		// We want to wait for the spawn result before calling the next loop
		const hasCallSucceeded = yield timerCall.done
		if (hasCallSucceeded) {
			yield call(delay, 2000)
		} else {
			yield call(delay, 5000)
		}
	}
}

function subscribe(socket) {
	return eventChannel(emit => {
		socket.on('timer', message => {
			emit({type: 'timer', message})
		})
		socket.on('disconnect', () => {
			emit({type: 'disconnect'})
		})
		return () => {}
	})
}

function completeConnection(socket, action) {
	return new Promise(function(resolve) {

		const removeListeners = () => {
			socket.off('disconnect', connected)
			socket.off('connected', disconnected)
		}

		const connected = () => {
			removeListeners()
			resolve(true)
		}

		const disconnected = () => {
			removeListeners()
			resolve(false)
		}

		socket.on('disconnect', disconnected)
		socket.on('connected', connected)

		socket.emit('join', {
			dui: action.session.device_uid,
			rd: action.session.userRd,
			session_uid: action.session.session_uid,
		})
	})
}

function *startSocketConnection(action) {
	let socket
	try {
		while (true) {
			socket = socketConnect(config.timer_socket)
			yield call(socket.connect)
			const connected = yield call(completeConnection, socket, action)
			if (connected) {
				return socket
			} else {
				yield call(delay, 10000)
			}
		}
	} catch (err) {
		socket.disconnect()
	} finally {
		if (yield cancelled()) {
			socket.disconnect()
		}
	}


}

function *getPollQuestionData(action) {

	const answers = yield call(
		getPollAnswersData,
		action.questionData,
		action.session,
		action.slide
	)

	yield put(
		studentActions.initializePollWithData(
			action.questionData,
			action.session,
			action.slide,
			action.slide_uid,
			answers
		)
	)
}

function *getQuizQuestionData(action) {
	const answers = yield call(
		getQuizAnswersData,
		action.questionData,
		action.session,
		action.slide
	)

	yield put(
		studentActions.initializeQuizWithData(
			action.questionData,
			action.session,
			action.slide,
			action.slide_uid,
			answers
		)
	)
}

function *getOEQSubmissionAudioData(action) {
	const audioData = {
		uid: '',
		url: '',
	}
	try {
		const extraData = yield call(
			getOeqExtraData,
			action.session,
			action.slide
		)
		if (extraData && extraData.audioUid) {
			const audioResponse = yield callAny(getStudentAudio, action.slide.slideId, action.session.headers)
			audioData.url = audioResponse.payload.audio.url
			audioData.uid = audioResponse.payload.audio.uid
		}
	} catch (err) {
	}
	return audioData
}

function *getOeqQuestionData(action) {
	const answers = yield call(
		getOeqAnswersData,
		action.questionData,
		action.session,
		action.slide
	)

	const audioData = yield call(getOEQSubmissionAudioData, action)

	yield put(
		studentActions.initializeOeqWithData(
			action.questionData,
			action.session,
			action.slide,
			action.slide_uid,
			answers,
			audioData,
		)
	)
}

function *getDrawitFromServer(slideId, headers) {
	try {
		const drawitResponse = yield callAny(
			studentSync.getStudentDrawit,
			slideId,
			headers,
		)

		return [{
			id: makeUniqueId(),
			index: 0,
			matrix: [1, 0, 0, 1, 1024/2, 768/2],
			rotate: 0,
			scale: 1,
			temp: false,
			type: 'image',
			xDir: 1,
			yDir: 1,
			file: {
				link: drawitResponse.payload.image.url,
			},
		}]
	} catch (err) {
		return []
	}
}

function *deleteOeqAudioRecording(action) {
	yield call(deleteFile, action.recordedAudio)
}

function *getDrawitQuestionData(action) {

	const answers = yield call(
		getDrawItAnswersData,
		action.questionData,
		action.session,
		action.slide
	)

	if (answers.actions.length === 0) {
		answers.actions = yield call(
			getDrawitFromServer,
			action.slide.slideId,
			action.session.headers,
		)
	}

	yield put(
		studentActions.initializeDrawItWithData(
			action.questionData,
			action.session,
			action.slide,
			action.slide_uid,
			answers.actions
		)
	)
}

function *subscribeToSocket(socket, action) {
	const channel = yield call(subscribe, socket)
	let isConnected = true
	while (isConnected) {
		const channel_message = yield take(channel)
		if (channel_message.type === 'disconnect') {
			isConnected = false
		} else if (channel_message.type === 'timer') {
			yield put(studentActions.timerUpdated(channel_message.message.status, action.slide_uid))
		}
	}
}

function *connectToTimer(action) {
	let polling
	let socket
	try {
		while (true) {
			polling = yield fork(startTimerPolling, action)
			socket = yield call(startSocketConnection, action)
			polling.cancel()
			// Once socket is connected we are requesting a single timer status
			// in case the message was delivered between polling iterations
			yield fork(getTimer, action)
			if (socket) {
				yield call(subscribeToSocket, socket, action)
				yield call(socket.disconnect)
			}

		}
	} finally {
		if (yield cancelled()) {
			if (polling) {
				polling.cancel()
			}
			if (socket) {
				yield call(socket.disconnect)
			}
		}
	}
}

function *initializeStatus() {
	while (true) {
		const action = yield take([
			actionTypes.STUDENT_INITIALIZE_POLL_WITH_DATA,
			actionTypes.STUDENT_INITIALIZE_QUIZ_WITH_DATA,
			actionTypes.STUDENT_INITIALIZE_OEQ_WITH_DATA,
			actionTypes.STUDENT_INITIALIZE_DRAW_IT_WITH_DATA,
			actionTypes.MATCHING_PAIRS_STUDENT_INITIALIZE,
		])
		const timerDisabledSessionTypes = [
			sessionTypes.HOMEWORK,
			sessionTypes.PREVIEW,
			sessionTypes.EMBED,
		]
		const isTimerDisabled = timerDisabledSessionTypes.includes(action.session.sessionType)
		if (!isTimerDisabled && action.slide.timer && action.slide.timer.active !== '0') {
			const connection = yield fork(connectToTimer, action)
			yield take([
				actionTypes.STUDENT_POLL_ENDED,
				actionTypes.STUDENT_QUIZ_ENDED,
				actionTypes.STUDENT_OEQ_ENDED,
				actionTypes.STUDENT_DRAW_IT_ENDED,
				actionTypes.MATCHING_PAIRS_STUDENT_ENDED,
			])
			yield cancel(connection)
		}
	}

}

function *matchingPairsStudentResponse() {
	const requestChannel = yield actionChannel(actionTypes.MATCHING_PAIRS_STUDENT_UPDATE, buffers.sliding(1))
	while (true) {
		const action = yield take(requestChannel)
		try {
			yield callAny(
				updateMatchingPairsStudentResponse,
				action.device_uid,
				action.slide,
				action.try_count,
				action.match_count,
				action.headers
			)
			yield call(delay, 5000)
		} catch (err) {
			const serverError = formatError(err)
			yield put(studentActions.studentMatchingPairsSubmissionFailed(serverError, action.slide_uid))
		}
	}
}

function *trackFailedSubmission(action) {

	const properties = {
		'Error Code': action.error.error_code,
		Message: action.error.message,
	}
	yield call(mixpanel.track, 'Student Submission Failed', properties, false)
}

// TODO: use this when doing multipart upload
/*
function *getRecordingUrlFromPhoenix() {
	yield call(delay, 10000)
	return 'delayed'
}

function flatteningOEQRecordingActivityBuffer() {

	const _actions = []
	return {
		put: action => {
			if (_actions.length
				&& _actions[_actions.length - 1].type === actionTypes.STUDENT_OEQ_RECORDING_UPDATE
				&& action.type === actionTypes.STUDENT_OEQ_RECORDING_UPDATE)
			{
				// Replace the latest action with the new one with updated chunks
				_actions[_actions.length - 1] = action
			} else {
				_actions.push(action)
			}
		},
		take: () => _actions.pop(),
		flush: () => {
			_actions.length = 0
		},
		isEmpty: () => _actions.length === 0,
	}
}

function *startOEQStudentRecord() {
	console.log('START')
	let lastUploadedChunk = 0
	const params = {
		Bucket: 'nearpod-student-audio-dev',
		Key: filename,
		ContentType: 'audio/webm',
		ACL: 'private',
	};
	const requestChannel = yield actionChannel([
		actionTypes.STUDENT_OEQ_RECORDING_UPDATE,
		actionTypes.STUDENT_OEQ_RECORDING_END,
	], flatteningOEQRecordingActivityBuffer())
	const delayer = yield call(getRecordingUrlFromPhoenix)
	console.log('delayer', delayer)
	while (true) {
		const action = yield take(requestChannel)
		try {
			console.log('action', action)
		} catch (err) {

		}
	}
}*/

function *saveOEQAudioFile(action) {
	try {
		const submitData = yield select(
			studentSelectors.studentAudioSelector,
			action.slide_uid
		)
		const audioSubmission = yield call(
			submitStudentAudio,
			action.chunks,
			submitData.device_uid,
			submitData.headers
		)
		yield put(
			studentActions.recordingSubmissionSuccess(
				audioSubmission.payload.audio.uid,
				action.id,
				action.slide_uid
			)
		)
	} catch (error) {
		yield put(studentActions.recordingSubmissionFailed(
			action.id,
			action.slide_uid
		))
	}
}

function *getLeadData(action) {
	try {
		const defaultData = {
			name: '',
			optionalName: '',
		}

		const cachedLead = yield call(
			getLead,
			action.session,
		)

		const cachedLastNicknameUserLoggedSigned = yield call(
			getLastNicknameUserLoggedSigned,
			action.session.pincode,
		)

		const leadData = cachedLead || defaultData

		yield put(
			studentActions.initializeLeadWithData(
				action.session,
				action.slide,
				action.slide_uid,
				leadData.name,
				leadData.optionalName,
				cachedLastNicknameUserLoggedSigned,
			)
		)
	} catch (error) {
		yield put(studentActions.initializeLeadFailed(error))
	}
}

function *saveLeadDataInCache(sessionData, name, optionalName) {
	try {
		const isUserLogged = yield select(userLoggedSelector)
		if (isUserLogged) {
			yield spawn(
				addLastNicknameUserLoggedSigned,
				sessionData.pincode,
				name,
			)
		}
		yield spawn(
			addLead,
			sessionData,
			name,
			optionalName,
		)
	} catch (err) {
		//no action
	}
}

function *submitLead(action) {
	try {
		if (action.sessionType !== sessionTypes.TEACHER_STUDENT_VIEW) {
			const submitData = yield select(studentSelectors.leadSubmitSelector, action.slide_uid)

			if (submitData.isValid) {
				const sessionData = yield select(studentSelectors.studentSessionDataSelector)
				const tooltipData = yield select(tooltipSelector)
				const isQuickTourExtendedActive = tooltipData.tooltips &&
					tooltipData.tooltips.tooltipOBQuickTourExtended &&
					action.sessionType === sessionTypes.LIVE_SESSION

				const name = submitData.name
				const optionalName = submitData.optionalName
				yield spawn(
					saveLeadDataInCache,
					sessionData,
					name,
					optionalName
				)

				const response = yield callAny(
					studentSync.submitLeadData,
					sessionData.device_uid,
					name,
					optionalName,
					sessionData.headers,
					action.ltiLisResult,
				)

				const {error_code, ...extraProps} = response

				const data = {
					error_code,
					payload: {
						...extraProps,
						...(isQuickTourExtendedActive && {
							title: localize('lead_page_quickTourExtended_modal_title'),
							show_welcome: '0',
						}),
					},
					...(isQuickTourExtendedActive && {
						message: localize('lead_page_quickTourExtended_modal_text'),
					}),
				}

				yield put(studentActions.leadSubmitSuccess(
					action.sessionType,
					sessionData.presentation_uid,
					sessionData.session_uid,
					data,
					action.slide_uid,
				))
			}
		}
	} catch (err) {
		const serverError = formatError(err)
		const sessionData = yield select(studentSelectors.studentSessionDataSelector)
		yield put(studentActions.leadSubmitFailed(
			action.sessionType,
			sessionData.presentation_uid,
			sessionData.session_uid,
			serverError,
			action.slide_uid,
		))
	}
}

function *googleSsoLogin(idToken) {
	return yield callAny(
		loginSync.submitGoogleToken,
		idToken
	)
}

function *office365SsoLogin(idToken) {
	return yield callAny(
		loginSync.submitOffice365Token,
		idToken
	)
}

function *submitLeadGoogleToken(action) {
	try {
		const response = yield call(googleSsoLogin, action.tokenData.id_token)
		yield put(studentActions.submitLeadGoogleTokenSuccess(response))
		// TODO: Code below mocks a regular lead submission. Remove when implementing the correct flow
		const sessionData = yield select(studentSelectors.studentSessionDataSelector)
		yield put(studentActions.leadSubmit(
			`${sessionData.session_uid}_0`,
			sessionData.sessionType,
			'',
		))
	} catch (err) {
		const serverError = formatError(err)
		yield put(studentActions.submitLeadGoogleTokenFailed(serverError))
	}
}

function *submitLeadOffice365Token(action) {
	try {
		const response = yield call(office365SsoLogin, action.tokenData.id_token)
		yield put(studentActions.submitLeadOffice365TokenSuccess(response))
		// TODO: Code below mocks a regular lead submission. Remove when implementing the correct flow
		const sessionData = yield select(studentSelectors.studentSessionDataSelector)
		yield put(studentActions.leadSubmit(
			`${sessionData.session_uid}_0`,
			sessionData.sessionType,
			'',
		))
	} catch (err) {
		const serverError = formatError(err)
		yield put(studentActions.submitLeadOffice365TokenFailed(serverError))
	}
}

function *submitStudentLoginGoogleToken(action) {
	try {
		const response = yield call(googleSsoLogin, action.tokenData.id_token)
		yield put(loginActions.studentSubmitLoginGoogleTokenSuccess(response))
	} catch (err) {
		const serverError = formatError(err)
		yield put(loginActions.studentSubmitLoginGoogleTokenFailed(serverError))
	}
}

function *submitStudentLoginOffice365Token(action) {
	try {
		const response = yield call(office365SsoLogin, action.tokenData.id_token)
		yield put(loginActions.studentSubmitLoginOffice365TokenSuccess(response))
	} catch (err) {
		const serverError = formatError(err)
		yield put(loginActions.studentSubmitLoginOffice365TokenFailed(serverError))
	}
}

function *autoSubmitLead(action) {
	if (action.session.isSessionPreview) {
		yield call(submitLead, {
			slide_uid: action.slide_uid,
			sessionType: action.session.sessionType,
			ltiLisResult: '',
		})
	}
}

export default [
	takeLatest([
		actionTypes.STUDENT_SUBMIT_POLL_ANSWERS,
		actionTypes.STUDENT_SUBMIT_POLL_CONFIRMED,
		actionTypes.STUDENT_TIMER_POLL_ENDED,
	], submitPollAnswers),
	takeLatest([
		actionTypes.STUDENT_QUIZ_CHANGE_SLIDE,
		actionTypes.STUDENT_QUIZ_SUBMIT_QUIZ,
		actionTypes.STUDENT_SUBMIT_QUIZ_CONFIRMED,
		actionTypes.STUDENT_TIMER_QUIZ_ENDED,
	], submitQuizAnswers),
	takeEvery([
		actionTypes.STUDENT_SUBMIT_OEQ_ANSWER,
		actionTypes.STUDENT_SUBMIT_OEQ_CONFIRMED,
		actionTypes.STUDENT_TIMER_OEQ_ENDED,
	], submitOeqAnswer),
	takeLatest(actionTypes.STUDENT_INITIALIZE_POLL, getPollQuestionData),
	takeLatest(actionTypes.STUDENT_INITIALIZE_DRAW_IT, getDrawitQuestionData),
	takeLatest(actionTypes.STUDENT_INITIALIZE_QUIZ, getQuizQuestionData),
	takeLatest(actionTypes.STUDENT_INITIALIZE_OEQ, getOeqQuestionData),
	takeLatest(actionTypes.STUDENT_INITIALIZE_LEAD, getLeadData),
	takeLatest(actionTypes.STUDENT_DELETE_AUDIO_OEQ_CONFIRMED, deleteOeqAudioRecording),
	// takeEvery(actionTypes.STUDENT_OEQ_RECORDING_START, startOEQStudentRecord),
	takeEvery(actionTypes.STUDENT_OEQ_RECORDING_UPDATE, saveOEQAudioFile),
	fork(initializeStatus),
	fork(matchingPairsStudentResponse),
	takeEvery([
		actionTypes.STUDENT_SUBMIT_FAILED,
		actionTypes.MATCHING_PAIRS_STUDENT_SUBMIT_FAILED,
	], trackFailedSubmission),
	fork(studentDrawingSubmissions),
	takeEvery(actionTypes.STUDENT_LEAD_SUBMIT, submitLead),
	takeLatest(actionTypes.STUDENT_LEAD_GOOGLE_LOGIN_SUCCESS, submitLeadGoogleToken),
	takeLatest(actionTypes.STUDENT_LEAD_OFFICE_365_LOGIN_SUCCESS, submitLeadOffice365Token),
	takeLatest(actionTypes.LOGIN_STUDENT_GOOGLE_LOGIN_SUCCESS, submitStudentLoginGoogleToken),
	takeLatest(actionTypes.LOGIN_STUDENT_OFFICE_365_LOGIN_SUCCESS, submitStudentLoginOffice365Token),
	takeLatest(actionTypes.STUDENT_INITIALIZE_LEAD_WITH_DATA, autoSubmitLead),
]

export {
	getTimer,
	startTimerPolling,
	submitPollAnswers,
	submitQuizAnswers,
	getPollQuestionData,
	getDrawitQuestionData,
	getQuizQuestionData,
	getLeadData,
	saveLeadDataInCache,
	submitLead,
	submitLeadGoogleToken,
	submitLeadOffice365Token,
	submitStudentLoginGoogleToken,
	submitStudentLoginOffice365Token,
}
